import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function PlanningAMoveArticle() {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          Planning a Move: A Guide to Packing, Organizing, and Donating
        </h1>
        <ArticleAuthor />
        <p>
          Moving is never a relaxing experience, but the way you pack can make
          it much easier. But a few tips, smart suggestions, and checklists
          should help cut the stress and get you ready for the big transition.
        </p>
        <h2 className={styles["Article__sub-heading"]}>General Packing Tips</h2>
        <p>
          First, keep your essentials, like clothes, phone chargers, snacks, and
          medications, in a suitcase. The suitcase will stand out from the
          boxes, making it easier to find, and it will keep everything you need
          for your first night in a new place together.
        </p>
        <p>
          Keep a small box with tools you may need as you move, like a hammer,
          screwdriver, and duct tape.
        </p>
        <p>
          Pack boxes with a soft layer at the very bottom. This could be
          newspaper or bubble wrap. It could also be towels, throw pillows, or
          blankets. Pack similar items together so you can easily find them
          later, and keep a protective layer, like more towels, newspaper, or
          bubble wrap, between them.
        </p>
        <p>
          Remember that it's better to make a few extra trips between the house
          and the car than for the bottom of a box to fall out. Don't overfill
          boxes, and be mindful of weight. Cardboard can only withstand so much.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.realsimple.com/home-organizing/organizing/moving/moving-packing-tips">
                17 Moving and Packing Tips
              </a>
              : Do it right and do it quickly with these simple ideas.
            </p>
          </li>
          <li>
            <p>
              <a href="https://teleport.org/blog/2016/06/packing-for-move-tips/">
                Eight Tips to Stay Sane While Moving
              </a>
              : Moving takes a toll on mental health, but you don't have to cry
              yourself to sleep.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.whattoexpect.com/toddler/family-changes-and-milestones/packing-up-a-house.aspx">
                Packing Up a House With Kids Around
              </a>
              : Kids make everything a little more challenging, especially
              moving.
            </p>
          </li>
          <li>
            <p>
              <a href="https://simplelivingmama.com/packing-tips-for-moving-a-large-family/">
                Packing Tips for Moving a Large Family
              </a>
              : More people means more stuff, but this guide can help keep you
              organized.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.caring.com/caregivers/senior-moving/">
                A Guide to Moving Seniors
              </a>
              : Moving elders is a big deal, but whether you're helping them
              move in or downsize, this guide will help ease the transition.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.themultitaskingmomsite.net/the-multitasking-mom/21-packing-tips-for-moving-a-family">
                21 Easy Packing Tips for Moving a Family
              </a>
              : Get the family together and get packing with these easy tips.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.mymovingreviews.com/move/family-moving-checklist/">
                The Epic Family Moving Checklist
              </a>
              : Do you have everything you need for every member of the family?
              This list will help.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.nytimes.com/2017/09/27/smarter-living/how-to-move-couples.html">
                How to Move as a Couple Without Driving Each Other Crazy
              </a>
              : True love faces obstacles, and the stress of moving can be a big
              one.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.uloop.com/news/view.php/233082/7-Things-to-Pack-in-Your-Overnight-Moving-Bag">
                Seven Things to Pack in Your Overnight Bag
              </a>
              : These seven items will help keep you calm, clean, and organized.
            </p>
          </li>
          <li>
            <p>
              <a href="https://ohmyapt.apartmentratings.com/moving-packing-first-night-bag.html">
                What to Pack in Your "First Night" Bag
              </a>
              : Moving typically takes all day, and your first night bag can
              ensure that you don't pull an all-nighter getting to the
              essentials.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.kentonline.co.uk/features/homes-gardens-and-interiors/how-to-pack-for-a-131802/">
                How to Pack in a Few Days
              </a>
              : In a rush? This article will help you move faster than you
              imagined.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Gathering Your Moving Supplies
        </h2>
        <p>
          There are three essential moving supplies: boxes, protective
          materials, and a dolly. You'll probably have to rent a dolly, but it's
          worth asking friends first to see if they have one you can borrow. No
          one wants to spend extra money on boxes and protective materials, but
          often, you can hunt down some for free.
        </p>
        <p>
          Remember to think creatively. You can move your clothes in a few old
          suitcases or borrow suitcases from a friend: They roll and are much
          easier to move than boxes. You can keep files and the contents of
          emptied drawers in canvas bags. If you still have boxes from small{" "}
          <a href="https://www.realestateagents.com/real-estate-agents/durham-nc">
            home
          </a>{" "}
          appliances, you can use them to protect those appliances in the move.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.homelight.com/blog/cheap-moving-boxes/">
                How to Find Cheap Moving Boxes
              </a>
              : Don't pay more than you have to for moving supplies!
            </p>
          </li>
          <li>
            <p>
              <a href="http://www.thebeautycynic.com/how-to-pack-for-cross-country-move/">
                How to Pack Only Two Suitcases for a Cross-Country Move
              </a>
              :{" "}
              <a href="https://www.realestateagents.com/">
                Hiring a real estate agent
              </a>{" "}
              and moving cross-country is an extra challenge. Can you do it with
              less?
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.consumeraffairs.com/movers/how-to-pack-for-move.html">
                How to Pack for a Move
              </a>
              : Even if you pack perfectly for every vacation, packing for a
              move requires special skills.
            </p>
          </li>
          <li>
            <p>
              <a href="https://home.howstuffworks.com/green-living/alternative-reusable-packing-materials.htm">
                Eight Alternative Packing Materials
              </a>
              : Want to save money or give the planet a break? These ideas will
              help.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.real-world-physics-problems.com/how-to-move-heavy-objects.html">
                How to Move Heavy Objects
              </a>
              : It's time to break out the high school physics book and learn
              the best way to move your biggest possessions.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Important Packing Tips
        </h2>
        <p>
          Prevent drops, breaks, and confusion with some advance planning. Make
          sure to label your boxes and keep an inventory, especially of
          expensive and personally important items. Knowing where your sheets
          and pillows are at the end of the day matters. No one wants to spend
          the evening tearing through every box to ensure that their jewelry
          collection is safe, either.
        </p>
        <p>
          Keep your list on your phone or a physical clipboard. A clipboard
          won't run out of batteries, but you'll always have your phone in your
          pocket, so if you're a worrier, use both.
        </p>
        <p>
          If you're hiring movers, give them advance warning about heavy or
          large items that don't fit in regular boxes. These include everything
          from your elliptical trainer to the hot tub out back. Failing to alert
          the movers means that they may have to partially unpack the moving van
          to make room, which will take extra time, or they may have to
          reschedule with more helpers and/or additional equipment.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.realtor.com/advice/move/things-you-need-to-do-to-prepare-for-the-movers/">
                Six Surprising Things You Need to Do to Prepare for Movers
              </a>
              : Make sure you're ready before the professionals show up.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.doityourself.com/stry/how-to-move-an-aboveground-pool">
                How to Move an Above-Ground Pool
              </a>
              : A pool is an investment, but can you take it with you when you
              move?
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.apartmenttherapy.com/3-smart-secrets-for-labeling-moving-boxes-that-will-make-your-next-move-a-breeze-222780">
                Three Smart Secrets for Labeling Your Boxes
              </a>
              : Know where everything is at all times with these clever ideas.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.huffpost.com/entry/8-things-not-to-pack-when-moving_b_8105014">
                Eight Things Not to Pack When Moving
              </a>
              : Skip these items to make your move smoother.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.neighbor.com/storage-blog/how-much-to-tip-movers/">
                How Much to Tip Movers
              </a>
              : Movers work hard, so be sure to tip them appropriately.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Packing Checklists by Room
        </h2>
        <h3>
          <b>Kitchen</b>
        </h3>
        <ul>
          <li>
            <p>Pots, pans, and skillets</p>
          </li>
          <li>
            <p>
              <a href="https://www.thekitchn.com/how-to-pack-glasses-dishes-moving-256955">
                Glasses
              </a>
              , mugs, and cups
            </p>
          </li>
          <li>
            <p>Small appliances (toaster, blender, coffeemaker, etc.)</p>
          </li>
          <li>
            <p>
              <a href="https://www.marthastewart.com/1530555/how-to-pack-dishes-when-moving">
                Plates
              </a>
            </p>
          </li>
          <li>
            <p>Serving utensils</p>
          </li>
          <li>
            <p>Cooking utensils</p>
          </li>
          <li>
            <p>Silverware</p>
          </li>
          <li>
            <p>Cleaning supplies</p>
          </li>
          <li>
            <p>Dry goods</p>
          </li>
          <li>
            <p>Defrost the refrigerator 48 hours before moving.</p>
          </li>
          <li>
            <p>
              Refrigerated and frozen foods{" "}
              <a href="https://www.freshoffthegrid.com/how-to-pack-a-cooler/">
                go in coolers
              </a>{" "}
              with ice.
            </p>
          </li>
        </ul>
        <h3>
          <b>Dining Room</b>
        </h3>
        <ul>
          <li>
            <p>Decorations</p>
          </li>
          <li>
            <p>Disassemble all furniture that comes apart.</p>
          </li>
          <li>
            <p>Wrap polished wood furniture in blankets for protection.</p>
          </li>
          <li>
            <p>Roll up rugs.</p>
          </li>
        </ul>
        <h3>
          <b>Living Room and Den</b>
        </h3>
        <ul>
          <li>
            <p>Books, DVDs, CDs, Blu-rays, etc.</p>
          </li>
          <li>
            <p>Pack electronics with extra protection.</p>
          </li>
          <li>
            <p>Remove light bulbs from lamps before moving them.</p>
          </li>
        </ul>
        <h3>
          <b>Bedrooms</b>
        </h3>
        <ul>
          <li>
            <p>Clothes</p>
          </li>
          <li>
            <p>Jewelry (should be kept with you)</p>
          </li>
          <li>
            <p>Garment bags and wardrobe boxes for formal clothes</p>
          </li>
          <li>
            <p>Protect bedding with plastic.</p>
          </li>
          <li>
            <p>Cover the mattress with a mattress bags</p>
          </li>
        </ul>
        <h3>
          <b>Bathrooms</b>
        </h3>
        <ul>
          <li>
            <p>Shower curtains, towels, bath mats</p>
          </li>
          <li>
            <p>Keep toiletries and cleaning supplies separate.</p>
          </li>
          <li>
            <p>Tape open containers closed.</p>
          </li>
        </ul>
        <h3>
          <b>Laundry Room</b>
        </h3>
        <ul>
          <li>
            <p>Tape appliance cords to the backs of appliances</p>
          </li>
          <li>
            <p>Pack dry cleaning goods together.</p>
          </li>
          <li>
            <p>
              <a href="https://www.insider.com/moving-advice-what-to-throw-away-2018-6">
                Consider throwing out
              </a>{" "}
              opened/mostly used laundry detergent.
            </p>
          </li>
        </ul>
        <h3>
          <b>Garage</b>
        </h3>
        <ul>
          <li>
            <p>Hand tools</p>
          </li>
          <li>
            <p>Power tools</p>
          </li>
          <li>
            <p>Deflated pool toys</p>
          </li>
          <li>
            <p>Drain gas from all machines.</p>
          </li>
          <li>
            <p>Consider throwing out hazardous/messy materials.</p>
          </li>
        </ul>
        <h3>
          <b>Home Office</b>
        </h3>
        <ul>
          <li>
            <p>Protect all electronics with bubble wrap.</p>
          </li>
          <li>
            <p>Pack attachments (mouse, webcam, keyboard) separately.</p>
          </li>
          <li>
            <p>Disassemble furniture.</p>
          </li>
          <li>
            <p>
              <a href="https://www.howtogeek.com/242428/whats-the-best-way-to-back-up-my-computer/">
                Back up all data
              </a>{" "}
              from your computer.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          What Packing Supplies Do I Need for Moving?
        </h2>
        <p>
          You'll need three big items: boxes, protective material, and a dolly.
          You can also use canvas bags for a lot of things, and large trash bags
          make great protection and transportation for soft home goods you don't
          use to{" "}
          <a href="https://www.bhg.com/home-improvement/moving/packing/packing-secrets-from-the-pros/">
            protect items
          </a>{" "}
          in boxes.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Where Can I Get Free Packing and Moving Boxes and Supplies?
        </h2>
        <p>
          Try getting materials for free by reaching out to friends. Chances are
          good that someone you know works in a big-box store and may be able to
          get some empty boxes for you. You can also find boxes and bubble wrap
          through social media (Facebook Marketplace is a great example) for
          free or cheap. Hold onto newspapers, magazines, and boxes used to ship
          online purchases in the weeks leading up to your move. Meal kit boxes
          are also a great size for moving.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.washingtonpost.com/lifestyle/home/when-to-hold-on-to-a-box-and-when-to-throw-it-away/2015/09/22/82b393e4-5bfa-11e5-9757-e49273f05f65_story.html">
                When to Keep a Box
              </a>
              : If you just got a new appliance, should you keep the box?
            </p>
          </li>
          <li>
            <p>
              <a href="https://thekrazycouponlady.com/tips/money/free-moving-boxes">
                29 Places to Get Free Moving Boxes
              </a>
              : Find clever ways to score free moving materials.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.apartmentlist.com/renter-life/13-places-to-get-free-moving-boxes">
                13 Places to Get Free Moving Materials
              </a>
              : Get even more free boxes and supplies with this list.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          How Do You Pack Dishes for a Move?
        </h2>
        <p>
          The more protective material you use, the better your dishes will
          weather the move. Between each layer, you need bubble wrap, fabric, or
          newspaper. This includes between stacked plates. Don't try to fill a
          whole box because dishes are heavy: With enough pressure, the tape on
          the bottom of the box will give way.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Where Can I Donate Used Clothing and Household Items?
        </h2>
        <p>
          Look around your immediate area to find regional charity shops and
          donation centers where you can dispose of things you don't want to
          bring with you. If you are part of a religious organization, ask if
          the organization is in touch with any charities that can use your
          secondhand materials.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.moneycrashers.com/where-donate-clothes-charity/">
                Where to Donate Used Clothes
              </a>
              : Don't throw away what you don't wear. Give it to someone in need
              instead.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.gogreendrop.com/redcross/clothing-donation-pickup">
                American Red Cross Clothing Donations
              </a>
              : Learn how to donate clothing to the Red Cross.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.habitat.org/restores/donate-goods">
                Donate Goods to Habitat for Humanity
              </a>
              : Habitat for Humanity accepts donations of clothes,{" "}
              <a href="https://www.realestateagents.com/real-estate-agents/raleigh-nc">
                home
              </a>{" "}
              goods, and more.
            </p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Where Can I Recycle Electronics I Don't Want Anymore?
        </h2>
        <p>
          Recycling electronics can be tricky, but there are some private
          businesses eager to take your old tech. Best Buy has an{" "}
          <a href="https://www.recyclingcenternear.me/best-buy-recycling/">
            electronics recycling program
          </a>
          , for instance, and you can find{" "}
          <a href="https://sidehusl.com/ecoatm/">ecoATMs</a> that accept smaller
          electronics all over the country.
        </p>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Planning a Move: A Guide to Packing, Organizing and Donating | Find a Contractor"
        }
        description={
          "Explore how you should plan your next move to limit stress and save time. Review our moving checklist now!"
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/planning-a-move-guide",
          },
          headline:
            "Planning a Move: A Guide to Packing, Organizing and Donating",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
